/**
 * KB-META
 * Purpose: Breadcrumb service
 * Role: ui.service
 * EntryPoints: constructor(), if(), for()
 * Inputs: private router:Router; private activatedRoute:ActivatedRoute; private location:Location; label:unknown; const child of children:unknown
 * Outputs: 
 * Validation: 
 * Dependencies: Router, ActivatedRoute, Location
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=services/breadcrumb.service; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';

export interface Breadcrumb {
  label: string;
  url: string;
}

// Define menu data interface
export interface MenuItem {
  key?: string;
  title?: string;
  url?: string;
  children?: MenuItem[];
}

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private breadcrumbs = new BehaviorSubject<Breadcrumb[]>([]);
  breadcrumbs$ = this.breadcrumbs.asObservable();
  
  private navigationHistory: string[] = [];
  private currentIndex = -1;
  
  // Menu data for breadcrumbs and help modal
  private menuData = new BehaviorSubject<MenuItem[]>([]);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location
  ) {
    // Track navigation history
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      // Check if current route is care-manager-dashboard
      if (event.urlAfterRedirects.includes('care-manager-dashboard')) {
        // Clear navigation history if we're on care-manager-dashboard
        this.navigationHistory = [event.urlAfterRedirects];
        this.currentIndex = 0;
      } else if (this.navigationHistory.length === 0 || 
          this.navigationHistory[this.currentIndex] !== event.urlAfterRedirects) {
        // Add current URL to history if it's different from the current one
        // Remove any forward history when navigating to a new location
        if (this.currentIndex < this.navigationHistory.length - 1) {
          this.navigationHistory = this.navigationHistory.slice(0, this.currentIndex + 1);
        }
        
        this.navigationHistory.push(event.urlAfterRedirects);
        this.currentIndex = this.navigationHistory.length - 1;
      }
      
      // Update breadcrumbs
      this.createBreadcrumbs(this.activatedRoute.root);
    });
    
    // Initialize menu data (this would typically come from an API or config)
    this.initializeMenuData();
  }

  private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: Breadcrumb[] = []): void {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      this.breadcrumbs.next(breadcrumbs);
      return;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      // Get the route's title from its data
      const label = child.snapshot.data['title'] || this.generateLabel(routeURL);
      
      if (label) {
        breadcrumbs.push({ label, url });
      }

      // Recursive call for nested routes
      this.createBreadcrumbs(child, url, breadcrumbs);
    }
  }

  // Generate a label from URL segment (capitalize and replace hyphens with spaces)
  private generateLabel(path: string): string {
    if (!path) return '';
    
    return path.split('-')
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  // Check if there's a previous page to navigate to and not on dashboard
  canGoBack(): boolean {
    // Don't show back button on the care-manager-dashboard
    if (this.navigationHistory.length > 0 && 
        this.navigationHistory[this.currentIndex].includes('care-manager-dashboard')) {
      return false;
    }
    return this.currentIndex > 0;
  }

  // Navigate back to the previous page
  goBack(): void {
    if (this.canGoBack()) {
      // Capture the target before moving the pointer.
      const current = this.navigationHistory[this.currentIndex] || '';
      const target = this.navigationHistory[this.currentIndex - 1] || '';

      // Patient Edit → Dashboard return: force a one-time refresh to avoid blank dashboard state.
      try {
        if (String(current).includes('patient-edit-2') && String(target).includes('care-manager-dashboard')) {
          sessionStorage.setItem('care-manager-dashboard:return-refresh-once', '1');
        }
      } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }

      this.currentIndex--;
      this.router.navigateByUrl(this.navigationHistory[this.currentIndex]);
    }
  }
  
  // Get menu data for help modal
  getMenuData(): Observable<MenuItem[]> {
    return this.menuData.asObservable();
  }
  
  // Update menu data
  setMenuData(menuData: MenuItem[]): void {
    this.menuData.next(menuData);
  }
  
  // Initialize menu data with default structure
  // This would typically be replaced with real data from your API
  private initializeMenuData(): void {
    const defaultMenuData: MenuItem[] = [
      {
        key: 'dashboard',
        title: 'Dashboard',
        url: '/dashboard',
        children: []
      },
      {
        key: 'content',
        title: 'Content',
        url: '/content',
        children: [
          {
            key: 'genusContentWall',
            title: 'genusContent Wall',
            url: '/page/content/manage-content-wall'
          },
          {
            key: 'addEditCaretile',
            title: 'Care Tiles',
            url: '/page/content/view-genus-tile'
          },
          {
            key: 'addEditContact',
            title: 'Contact Tiles',
            url: '/page/content/view-contact-tile'
          },
          {
            key: 'addEditQuestion',
            title: 'Questions',
            url: '/page/content/view-question'
          },
          {
            key: 'addEditTileBlock',
            title: 'Tile Blocks',
            url: '/page/content/view-pathway'
          }
        ]
      },
      {
        key: 'team',
        title: 'Team',
        url: '/team',
        children: [
          {
            key: 'addUser',
            title: 'Add User',
            url: '/page/team/add-user'
          }
        ]
      },
      {
        key: 'genus-connect',
        title: 'Genus Connect',
        url: '/genus-connect',
        children: [
          {
            key: 'communityDetail',
            title: 'Community Detail',
            url: '/page/genus-connect/view-community'
          },
          {
            key: 'addCommunity',
            title: 'Add Community',
            url: '/page/genus-connect/add-community'
          }
        ]
      },
      {
        key: 'setting',
        title: 'Settings',
        url: '/setting',
        children: [
          {
            key: 'editPartner',
            title: 'Partner Settings',
            url: '/page/setting/view-partner'
          }
        ]
      }
    ];
    
    this.menuData.next(defaultMenuData);
  }
} 